import React, { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BottomNav } from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { Gem, Star, Gift, Sparkles, ArrowRight, Lock, Check, ChevronDown, ChevronUp, X, AlertCircle } from 'lucide-react';
interface ShopPageProps {
  onNavigate: (page: string) => void;
  onOpenRewardDetail: (rewardId: string) => void;
}
interface Voucher {
  id: string;
  name: string;
  logo: string;
  value: number;
  cost: number;
  description: string;
}
interface StarReward {
  id: string;
  title: string;
  description: string;
  cost: number;
  icon: string;
  unlocked: boolean;
}
const vouchers: Voucher[] = [{
  id: 'naturasi-10',
  name: 'NaturaSì',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2d/Naturas%C3%AC_Logo.svg/1200px-Naturas%C3%AC_Logo.svg.png',
  value: 10,
  cost: 100,
  description: 'Buono da €10 per spesa bio su NaturaSì (online o in punto vendita)'
}, {
  id: 'viviverde-coop-15',
  name: 'ViviVerde Coop',
  logo: 'https://static.tecnichenuove.it/gdoweek/2014/10/vivi_verde_coop_logo_free.jpg',
  value: 15,
  cost: 150,
  description: 'Buono da €15 su selezione ViviVerde Coop (cura casa e persona sostenibili)'
}, {
  id: 'lasaponaria-20',
  name: 'La Saponaria',
  logo: 'https://blog.lasaponaria.it/hubfs/LaSaponaria-5.jpg',
  value: 20,
  cost: 200,
  description: 'Buono da €20 per cosmetici bio e prodotti plastic-free La Saponaria'
}, {
  id: 'verso-natura-conad-25',
  name: 'Verso Natura Conad',
  logo: 'https://www.conadreggioemilia.it/wp-content/uploads/2018/04/conad-verso-natura.jpg',
  value: 25,
  cost: 250,
  description: 'Buono da €25 su prodotti Verso Natura Conad (spesa consapevole)'
}, {
  id: 'biobottega-50',
  name: 'Biobottega',
  logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRK8fCF7XAKaz98RXXgf7lhNJ2Gs_HK8Ikn4w&s',
  value: 50,
  cost: 500,
  description: 'Buono da €50 per spesa bio e prodotti naturali Biobottega (online o in negozio)'
}];
const starRewards: StarReward[] = [{
  id: 'premium',
  title: 'Greenback Premium',
  description: 'Sblocca statistiche avanzate e badge esclusivi',
  cost: 500,
  icon: '✨',
  unlocked: false
}, {
  id: 'event1',
  title: 'Evento Community Mensile',
  description: 'Accesso prioritario agli eventi della community',
  cost: 300,
  icon: '🎉',
  unlocked: false
}, {
  id: 'content',
  title: 'Contenuti Esclusivi',
  description: 'Accesso a guide e contenuti premium sulla sostenibilità',
  cost: 200,
  icon: '📚',
  unlocked: true
}, {
  id: 'workshop',
  title: 'Workshop Sostenibilità',
  description: 'Partecipazione a workshop online con esperti',
  cost: 400,
  icon: '🎓',
  unlocked: false
}];
export function ShopPage({
  onNavigate,
  onOpenRewardDetail
}: ShopPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'diamond' | 'star'>('diamond');
  const [showMyRewards, setShowMyRewards] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showStarConfirmModal, setShowStarConfirmModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedStarReward, setSelectedStarReward] = useState<StarReward | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isStarClosing, setIsStarClosing] = useState(false);
  const [isCashClosing, setIsCashClosing] = useState(false);
  const {
    wallet,
    claimedRewards,
    claimReward,
    showToast
  } = useApp();
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 20);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  const handleOpenConfirmModal = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setShowConfirmModal(true);
  };
  const handleCloseConfirmModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowConfirmModal(false);
      setSelectedVoucher(null);
      setIsClosing(false);
    }, 300);
  };
  const handleOpenStarConfirmModal = (reward: StarReward) => {
    setSelectedStarReward(reward);
    setShowStarConfirmModal(true);
  };
  const handleCloseStarConfirmModal = () => {
    setIsStarClosing(true);
    setTimeout(() => {
      setShowStarConfirmModal(false);
      setSelectedStarReward(null);
      setIsStarClosing(false);
    }, 300);
  };
  const handleOpenCashModal = () => {
    setShowCashModal(true);
  };
  const handleCloseCashModal = () => {
    setIsCashClosing(true);
    setTimeout(() => {
      setShowCashModal(false);
      setIsCashClosing(false);
    }, 300);
  };
  const handleConfirmPurchase = () => {
    if (!selectedVoucher) return;
    const claimedId = claimReward(selectedVoucher.id);
    showToast({
      variant: 'success',
      message: 'Premio ottenuto!'
    });
    handleCloseConfirmModal();
    onOpenRewardDetail(claimedId);
  };
  const handleConfirmStarUnlock = () => {
    if (!selectedStarReward) return;
    showToast({
      variant: 'success',
      message: 'Ricompensa sbloccata!'
    });
    handleCloseStarConfirmModal();
  };
  const handleConfirmCashConversion = () => {
    showToast({
      variant: 'success',
      message: 'Richiesta inviata!'
    });
    handleCloseCashModal();
  };
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short'
    });
  };
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1427">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-1428">
        <div className="flex items-center" data-id="element-1429">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-1430" />
        </div>
        <div className="flex items-center gap-3" data-id="element-1431">
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-1432">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-1433" />
            <span className="text-sm font-normal" data-id="element-1434">{wallet.diamonds}</span>
          </div>
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-1435">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-1436" />
            <span className="text-sm font-normal" data-id="element-1437">{wallet.stars}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-1438">
        {/* Hero Background */}
        <div className="bg-green-600 w-full relative pt-20 pb-10" data-id="element-1439">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-1440" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-1441">
            <div className="flex items-center justify-between" data-id="element-1442">
              <div data-id="element-1443">
                <h2 className="font-normal text-2xl text-white mb-1" data-id="element-1444">Premi</h2>
                <p className="text-sm text-white/80" data-id="element-1445">Converti i tuoi crediti</p>
              </div>
              <Gift className="h-8 w-8 text-white" strokeWidth={1.5} data-id="element-1446" />
            </div>
          </div>
        </div>

        <div className="px-6 pb-28 max-w-md mx-auto relative" data-id="element-1447">
          {/* Tab Switcher */}
          <div className="relative -mt-5 mb-4" data-id="element-1448">
            <div className="bg-white p-1 rounded-full border border-gray-200 flex" data-id="element-1449">
              <button onClick={() => setActiveTab('diamond')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'diamond' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-1450">
                <Gem className="h-4 w-4" strokeWidth={2} data-id="element-1451" />
                <span data-id="element-1452">Crediti Diamond</span>
              </button>
              <button onClick={() => setActiveTab('star')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'star' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-1453">
                <Star className="h-4 w-4" strokeWidth={2} data-id="element-1454" />
                <span data-id="element-1455">Crediti Stelle</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6" data-id="element-1456">
            {activeTab === 'diamond' ? <div className="space-y-6" data-id="element-1457">
                {/* Balance Card */}
                <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-5 text-white" data-id="element-1458">
                  <div className="flex items-center justify-between mb-3" data-id="element-1459">
                    <div data-id="element-1460">
                      <p className="text-sm text-white/80 mb-1" data-id="element-1461">
                        Saldo disponibile
                      </p>
                      <div className="flex items-center gap-2" data-id="element-1462">
                        <Gem className="h-6 w-6" strokeWidth={2} data-id="element-1463" />
                        <span className="text-3xl font-normal" data-id="element-1464">
                          {wallet.diamonds}
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1465">
                      <p className="text-xs text-white/60" data-id="element-1466">Valore stimato</p>
                      <p className="text-lg font-normal" data-id="element-1467">
                        €{wallet.diamonds / 10}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-white/70" data-id="element-1468">
                    1 Diamond = €0.10 • Convertibili in buoni acquisto o denaro
                  </p>
                </div>

                {/* My Rewards Section */}
                {claimedRewards.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1469">
                    <button onClick={() => setShowMyRewards(!showMyRewards)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1470">
                      <div className="flex items-center gap-2" data-id="element-1471">
                        <Gift className="h-5 w-5 text-green-600" data-id="element-1472" />
                        <h3 className="text-base font-normal text-gray-900" data-id="element-1473">
                          I tuoi premi
                        </h3>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full" data-id="element-1474">
                          {claimedRewards.length}
                        </span>
                      </div>
                      {showMyRewards ? <ChevronUp className="h-5 w-5 text-gray-400" data-id="element-1475" /> : <ChevronDown className="h-5 w-5 text-gray-400" data-id="element-1476" />}
                    </button>

                    {showMyRewards && <div className="px-3 pb-3 space-y-2" data-id="element-1477">
                        {claimedRewards.map(reward => <button key={reward.id} onClick={() => onOpenRewardDetail(reward.id)} className="w-full bg-gray-50 rounded-xl p-3 border border-gray-100 hover:border-green-500 transition-all text-left" data-id="element-1478">
                            <div className="flex items-center gap-3" data-id="element-1479">
                              {reward.logo && <div className="w-12 h-12 bg-white rounded-lg border border-gray-100 flex items-center justify-center flex-shrink-0 p-1.5" data-id="element-1480">
                                  <img src={reward.logo} alt={reward.name} className="w-full h-full object-contain" data-id="element-1481" />
                                </div>}
                              <div className="flex-1 min-w-0" data-id="element-1482">
                                <h4 className="font-normal text-sm text-gray-900 mb-1" data-id="element-1483">
                                  {reward.name}
                                </h4>
                                <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1484">
                                  <span data-id="element-1485">€{reward.value}</span>
                                  <span data-id="element-1486">•</span>
                                  <span data-id="element-1487">
                                    Scade il {formatDate(reward.expiresAt)}
                                  </span>
                                </div>
                              </div>
                              <ArrowRight className="h-4 w-4 text-gray-400" data-id="element-1488" />
                            </div>
                          </button>)}
                      </div>}
                  </div>}

                {/* Vouchers Section */}
                <div data-id="element-1489">
                  <h3 className="text-lg font-normal text-gray-900 mb-4" data-id="element-1490">
                    Buoni Acquisto
                  </h3>
                  <div className="space-y-3" data-id="element-1491">
                    {vouchers.map(voucher => {
                  const canAfford = wallet.diamonds >= voucher.cost;
                  return <div key={voucher.id} className={`bg-white rounded-xl p-4 border transition-all ${canAfford ? 'border-gray-200 hover:border-green-500' : 'border-gray-100 opacity-60'}`} data-id="element-1492">
                          <div className="flex items-start gap-4 mb-3" data-id="element-1493">
                            <div className="w-16 h-16 bg-white rounded-lg border border-gray-100 flex items-center justify-center flex-shrink-0 p-2" data-id="element-1494">
                              <img src={voucher.logo} alt={voucher.name} className="w-full h-full object-contain" data-id="element-1495" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1496">
                              <h4 className="font-normal text-sm text-gray-900 mb-1" data-id="element-1497">
                                {voucher.name}
                              </h4>
                              <p className="text-xs text-gray-500 mb-2" data-id="element-1498">
                                {voucher.description}
                              </p>
                              <div className="flex items-center gap-2" data-id="element-1499">
                                <div className="flex items-center gap-1 text-xs font-normal text-green-600" data-id="element-1500">
                                  <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1501" />
                                  <span data-id="element-1502">{voucher.cost}</span>
                                </div>
                                <span className="text-xs text-gray-400" data-id="element-1503">•</span>
                                <span className="text-xs text-gray-600" data-id="element-1504">
                                  Valore €{voucher.value}
                                </span>
                              </div>
                            </div>
                          </div>
                          {canAfford ? <button onClick={() => handleOpenConfirmModal(voucher)} className="w-full py-2.5 rounded-full text-sm font-normal bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center gap-2" data-id="element-1505">
                              Converti
                              <ArrowRight className="h-4 w-4" data-id="element-1506" />
                            </button> : <div className="w-full py-2.5 rounded-full text-sm font-normal bg-gray-100 text-gray-400 flex items-center justify-center gap-2" data-id="element-1507">
                              <Lock className="h-4 w-4" data-id="element-1508" />
                              Bloccato
                            </div>}
                        </div>;
                })}
                  </div>
                </div>

                {/* Cash Conversion */}
                <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-1509">
                  <div className="flex items-start gap-3 mb-4" data-id="element-1510">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1511">
                      <span className="text-2xl" data-id="element-1512">💰</span>
                    </div>
                    <div className="flex-1" data-id="element-1513">
                      <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-1514">
                        Converti in denaro
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1515">
                        Trasferisci i tuoi Diamond direttamente sul tuo conto
                        bancario o PayPal
                      </p>
                    </div>
                  </div>
                  <button onClick={handleOpenCashModal} className="w-full py-3 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-colors" data-id="element-1516">
                    Richiedi conversione
                  </button>
                </div>
              </div> : <div className="space-y-6" data-id="element-1517">
                {/* Balance Card */}
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-5 text-white" data-id="element-1518">
                  <div className="mb-3" data-id="element-1519">
                    <p className="text-sm text-white/80 mb-1" data-id="element-1520">
                      Saldo disponibile
                    </p>
                    <div className="flex items-center gap-2" data-id="element-1521">
                      <Star className="h-6 w-6" strokeWidth={2} fill="white" data-id="element-1522" />
                      <span className="text-3xl font-normal" data-id="element-1523">
                        {wallet.stars}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-white/70" data-id="element-1524">
                    Sblocca vantaggi esclusivi e partecipa a eventi speciali
                  </p>
                </div>

                {/* Star Rewards */}
                <div data-id="element-1525">
                  <h3 className="text-lg font-normal text-gray-900 mb-4" data-id="element-1526">
                    Ricompense Disponibili
                  </h3>
                  <div className="space-y-3" data-id="element-1527">
                    {starRewards.map(reward => {
                  const canAfford = wallet.stars >= reward.cost;
                  return <div key={reward.id} className={`bg-white rounded-xl p-4 border transition-all ${reward.unlocked ? 'border-green-200 bg-green-50' : canAfford ? 'border-gray-200 hover:border-green-500 cursor-pointer' : 'border-gray-100 opacity-60'}`} data-id="element-1528">
                          <div className="flex items-start gap-3" data-id="element-1529">
                            <div className="w-12 h-12 bg-white rounded-xl border border-gray-100 flex items-center justify-center flex-shrink-0 text-2xl" data-id="element-1530">
                              {reward.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1531">
                              <h4 className="font-normal text-sm text-gray-900 mb-1" data-id="element-1532">
                                {reward.title}
                              </h4>
                              <p className="text-xs text-gray-600 leading-relaxed mb-3" data-id="element-1533">
                                {reward.description}
                              </p>
                              <div className="flex items-center justify-between" data-id="element-1534">
                                <div className="flex items-center gap-1 text-xs font-normal text-gray-600" data-id="element-1535">
                                  <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1536" />
                                  <span data-id="element-1537">{reward.cost}</span>
                                </div>
                                {reward.unlocked ? <div className="px-4 py-1.5 rounded-full text-xs font-normal bg-gray-100 text-gray-600 flex items-center gap-1" data-id="element-1538">
                                    <Check className="h-3 w-3" data-id="element-1539" />
                                    <span data-id="element-1540">Sbloccato</span>
                                  </div> : canAfford ? <button onClick={() => handleOpenStarConfirmModal(reward)} className="px-4 py-1.5 rounded-full text-xs font-normal bg-green-600 text-white hover:bg-green-700 transition-colors" data-id="element-1541">
                                    Sblocca
                                  </button> : <div className="flex items-center gap-1 text-xs font-normal text-gray-400" data-id="element-1542">
                                    <Lock className="h-3 w-3" data-id="element-1543" />
                                    <span data-id="element-1544">Bloccato</span>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-1545">
                  <div className="flex items-start gap-3" data-id="element-1546">
                    <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1547" />
                    <div data-id="element-1548">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1549">
                        <span className="font-medium" data-id="element-1550">Nota:</span> I Crediti
                        Stelle hanno valore non monetario e possono essere
                        utilizzati per sbloccare funzionalità premium, accedere
                        a eventi esclusivi e contenuti riservati della community
                        Greenback.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </ScrollArea>

      <BottomNav activePage="market" onNavigate={onNavigate} data-id="element-1551" />

      {/* Diamond Confirmation Modal */}
      {showConfirmModal && selectedVoucher && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseConfirmModal} data-id="element-1552" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-1553">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1554">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1555" />
              <div className="flex items-center justify-between" data-id="element-1556">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1557">
                  Conferma acquisto
                </h3>
                <button onClick={handleCloseConfirmModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1558">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1559" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1560">
              <div className="space-y-6" data-id="element-1561">
                {/* Reward Preview */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1562">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1563">
                    <div className="w-20 h-20 bg-white rounded-lg border border-gray-100 flex items-center justify-center flex-shrink-0 p-2" data-id="element-1564">
                      <img src={selectedVoucher.logo} alt={selectedVoucher.name} className="w-full h-full object-contain" data-id="element-1565" />
                    </div>
                    <div className="flex-1" data-id="element-1566">
                      <h4 className="font-normal text-base text-gray-900 mb-2" data-id="element-1567">
                        {selectedVoucher.name}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1568">
                        {selectedVoucher.description}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-1569">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1570">Cosa riceverai</p>
                    <p className="text-sm font-normal text-gray-900" data-id="element-1571">
                      Buono acquisto del valore di €{selectedVoucher.value}
                    </p>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-1572">
                  <div className="flex items-center justify-between py-3" data-id="element-1573">
                    <span className="text-sm text-gray-600" data-id="element-1574">Costo</span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1575">
                      <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-1576" />
                      <span data-id="element-1577">-{selectedVoucher.cost}</span>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-1578" />

                  <div className="flex items-center justify-between py-3" data-id="element-1579">
                    <span className="text-sm text-gray-600" data-id="element-1580">Saldo attuale</span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1581">
                      <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-1582" />
                      <span data-id="element-1583">{wallet.diamonds}</span>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-1584" />

                  <div className="flex items-center justify-between py-3" data-id="element-1585">
                    <span className="text-sm text-gray-600" data-id="element-1586">
                      Saldo dopo l'acquisto
                    </span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1587">
                      <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-1588" />
                      <span data-id="element-1589">{wallet.diamonds - selectedVoucher.cost}</span>
                    </div>
                  </div>
                </div>

                {/* Warning Box */}
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200" data-id="element-1590">
                  <div className="flex items-start gap-3" data-id="element-1591">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1592" />
                    <div data-id="element-1593">
                      <p className="text-xs text-yellow-900 leading-relaxed" data-id="element-1594">
                        <span className="font-medium" data-id="element-1595">Attenzione:</span> Una
                        volta confermato l'acquisto, i Punti Diamond verranno
                        scalati immediatamente e il premio sarà disponibile
                        nella sezione "I tuoi premi".
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white space-y-3" data-id="element-1596">
              <button onClick={handleConfirmPurchase} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-1597">
                Conferma acquisto
              </button>
              <button onClick={handleCloseConfirmModal} className="w-full py-3 px-4 rounded-full text-sm font-normal text-gray-600 bg-white border-2 border-gray-200 hover:bg-gray-50 transition-all" data-id="element-1598">
                Annulla
              </button>
            </div>
          </div>
        </>}

      {/* Star Reward Confirmation Modal */}
      {showStarConfirmModal && selectedStarReward && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseStarConfirmModal} data-id="element-1599" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isStarClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-1600">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1601">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1602" />
              <div className="flex items-center justify-between" data-id="element-1603">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1604">
                  Sblocca ricompensa
                </h3>
                <button onClick={handleCloseStarConfirmModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1605">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1606" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1607">
              <div className="space-y-6" data-id="element-1608">
                {/* Reward Preview */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1609">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1610">
                    <div className="w-16 h-16 bg-white rounded-xl border border-gray-100 flex items-center justify-center flex-shrink-0 text-3xl" data-id="element-1611">
                      {selectedStarReward.icon}
                    </div>
                    <div className="flex-1" data-id="element-1612">
                      <h4 className="font-normal text-base text-gray-900 mb-2" data-id="element-1613">
                        {selectedStarReward.title}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1614">
                        {selectedStarReward.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-1615">
                  <div className="flex items-center justify-between py-3" data-id="element-1616">
                    <span className="text-sm text-gray-600" data-id="element-1617">Costo</span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1618">
                      <Star className="h-4 w-4 text-gray-600" strokeWidth={2} data-id="element-1619" />
                      <span data-id="element-1620">-{selectedStarReward.cost}</span>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-1621" />

                  <div className="flex items-center justify-between py-3" data-id="element-1622">
                    <span className="text-sm text-gray-600" data-id="element-1623">Saldo attuale</span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1624">
                      <Star className="h-4 w-4 text-gray-600" strokeWidth={2} data-id="element-1625" />
                      <span data-id="element-1626">{wallet.stars}</span>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-1627" />

                  <div className="flex items-center justify-between py-3" data-id="element-1628">
                    <span className="text-sm text-gray-600" data-id="element-1629">
                      Saldo dopo lo sblocco
                    </span>
                    <div className="flex items-center gap-1 text-sm font-normal text-gray-900" data-id="element-1630">
                      <Star className="h-4 w-4 text-gray-600" strokeWidth={2} data-id="element-1631" />
                      <span data-id="element-1632">{wallet.stars - selectedStarReward.cost}</span>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-1633">
                  <div className="flex items-start gap-3" data-id="element-1634">
                    <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1635" />
                    <div data-id="element-1636">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1637">
                        Una volta sbloccata, questa ricompensa sarà attiva
                        immediatamente e potrai usufruire dei vantaggi inclusi.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white space-y-3" data-id="element-1638">
              <button onClick={handleConfirmStarUnlock} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-1639">
                Sblocca ricompensa
              </button>
              <button onClick={handleCloseStarConfirmModal} className="w-full py-3 px-4 rounded-full text-sm font-normal text-gray-600 bg-white border-2 border-gray-200 hover:bg-gray-50 transition-all" data-id="element-1640">
                Annulla
              </button>
            </div>
          </div>
        </>}

      {/* Cash Conversion Modal */}
      {showCashModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCashModal} data-id="element-1641" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isCashClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-1642">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-1643">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-1644" />
              <div className="flex items-center justify-between" data-id="element-1645">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-1646">
                  Conversione in denaro
                </h3>
                <button onClick={handleCloseCashModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1647">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-1648" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1649">
              <div className="space-y-6" data-id="element-1650">
                {/* Amount Preview */}
                <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-5 text-white text-center" data-id="element-1651">
                  <p className="text-sm text-white/80 mb-2" data-id="element-1652">
                    Importo disponibile
                  </p>
                  <p className="text-4xl font-normal mb-1" data-id="element-1653">
                    €{wallet.diamonds / 10}
                  </p>
                  <div className="flex items-center justify-center gap-1 text-xs text-white/70" data-id="element-1654">
                    <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-1655" />
                    <span data-id="element-1656">{wallet.diamonds} Punti Diamond</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div data-id="element-1657">
                  <h4 className="text-sm font-normal text-gray-900 mb-3" data-id="element-1658">
                    Metodo di pagamento
                  </h4>
                  <div className="space-y-3" data-id="element-1659">
                    <button className="w-full bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-green-500 transition-all text-left" data-id="element-1660">
                      <div className="flex items-center gap-3" data-id="element-1661">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1662">
                          <span className="text-2xl" data-id="element-1663">💳</span>
                        </div>
                        <div className="flex-1" data-id="element-1664">
                          <p className="font-normal text-sm text-gray-900" data-id="element-1665">
                            PayPal
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1666">
                            Trasferimento istantaneo
                          </p>
                        </div>
                      </div>
                    </button>

                    <button className="w-full bg-white rounded-xl p-4 border-2 border-gray-200 hover:border-green-500 transition-all text-left" data-id="element-1667">
                      <div className="flex items-center gap-3" data-id="element-1668">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1669">
                          <span className="text-2xl" data-id="element-1670">🏦</span>
                        </div>
                        <div className="flex-1" data-id="element-1671">
                          <p className="font-normal text-sm text-gray-900" data-id="element-1672">
                            Bonifico bancario
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1673">
                            2-3 giorni lavorativi
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-1674">
                  <div className="flex items-start gap-3" data-id="element-1675">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1676" />
                    <div data-id="element-1677">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1678">
                        <span className="font-medium" data-id="element-1679">Nota:</span> La richiesta
                        verrà elaborata entro 48 ore. Riceverai una conferma via
                        email con i dettagli del trasferimento.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white space-y-3" data-id="element-1680">
              <button onClick={handleConfirmCashConversion} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-1681">
                Conferma richiesta
              </button>
              <button onClick={handleCloseCashModal} className="w-full py-3 px-4 rounded-full text-sm font-normal text-gray-600 bg-white border-2 border-gray-200 hover:bg-gray-50 transition-all" data-id="element-1682">
                Annulla
              </button>
            </div>
          </div>
        </>}
    </div>;
}